import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Bot, Check, Database, Send, Share2, Layers, Cpu, Home, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const BetaSimulation = () => {
    const [step, setStep] = useState(1);
    const navigate = useNavigate();

    const nextStep = () => setStep(s => Math.min(s + 1, 5));

    return (
        <div className="min-h-screen bg-black text-white overflow-hidden relative font-sans">
            {/* Navbar */}
            <div className="absolute top-0 left-0 right-0 p-4 md:p-6 flex justify-between items-center z-50">
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center font-bold">I</div>
                    <span className="font-bold tracking-tight text-sm md:text-base">PredictClaw <span className="text-primary">BETA</span></span>
                </div>
                <button onClick={() => navigate('/')} className="p-2 hover:bg-white/10 rounded-full transition-colors"><Home size={20} /></button>
            </div>

            {/* Progress Bar */}
            <div className="absolute top-20 md:top-24 left-0 right-0 container mx-auto px-4 z-40">
                <div className="flex justify-between items-center max-w-3xl mx-auto mb-4 text-[10px] md:text-xs font-mono text-muted-foreground uppercase tracking-widest hidden md:flex">
                    <span className={step >= 1 ? "text-primary" : ""}>Trigger</span>
                    <span className={step >= 2 ? "text-primary" : ""}>Neural Scan</span>
                    <span className={step >= 3 ? "text-secondary" : ""}>Lobster Consensus</span>
                    <span className={step >= 4 ? "text-secondary" : ""}>Cross-Chain</span>
                    <span className={step >= 5 ? "text-secondary" : ""}>Profit</span>
                </div>
                {/* Mobile minimal text */}
                <div className="md:hidden text-center text-xs font-mono text-muted-foreground mb-2">
                    STEP {step} / 5
                </div>

                <div className="h-1 bg-white/10 max-w-3xl mx-auto rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-gradient-to-r from-primary via-primary to-secondary"
                        initial={{ width: "0%" }}
                        animate={{ width: `${(step / 5) * 100}%` }}
                        transition={{ duration: 0.5 }}
                    />
                </div>
            </div>

            {/* Main Content Area */}
            <div className="min-h-screen pt-32 pb-12 container mx-auto px-4 flex flex-col items-center justify-center relative">
                <AnimatePresence mode="wait">
                    {step === 1 && <StepInput key="step1" onNext={nextStep} />}
                    {step === 2 && <StepNeural key="step2" onNext={nextStep} />}
                    {step === 3 && <StepLobster key="step3" onNext={nextStep} />}
                    {step === 4 && <StepLiquidity key="step4" onNext={nextStep} />}
                    {step === 5 && <StepResult key="step5" onRestart={() => setStep(1)} />}
                </AnimatePresence>
            </div>
        </div>
    );
};

// --- Step 1: Telegram Input ---
const StepInput = ({ onNext }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="w-full max-w-md px-4"
        >
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary/20 text-secondary mb-6">
                    <Send size={32} />
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-2">Initialize Protocol</h2>
                <p className="text-gray-400 text-sm md:text-base">Send a command to the PredictClaw Agent via Telegram.</p>
            </div>

            <div className="bg-[#1E2329] border border-white/10 rounded-2xl p-4 md:p-6 shadow-2xl relative overflow-hidden group hover:border-secondary/30 transition-colors cursor-pointer" onClick={onNext}>
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-secondary to-secondary/80" />
                    <div>
                        <div className="font-bold text-white">System Admin</div>
                        <div className="text-xs text-secondary">online</div>
                    </div>
                </div>
                <div className="bg-secondary/20 self-end rounded-2xl rounded-tr-sm p-3 md:p-4 text-white mb-4 ml-auto max-w-[85%] relative text-sm md:text-base">
                    /predict Which crypto will flip ETH market cap...
                    <div className="absolute -right-2 top-0 w-3 h-3 bg-secondary/20 [clip-path:polygon(0_0,0%_100%,100%_0)]" />
                </div>

                <div className="mt-8 pt-4 border-t border-white/5 flex gap-2">
                    <div className="bg-black/50 h-10 flex-1 rounded-lg" />
                    <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                        <Send size={18} />
                    </div>
                </div>

                {/* Cursor Click Hint */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                    className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[2px]"
                >
                    <button className="px-6 py-3 bg-secondary hover:bg-secondary/80 rounded-full font-bold shadow-[0_0_30px_rgba(0,212,170,0.4)] flex items-center gap-2 text-sm md:text-base text-black">
                        Click to Send <ArrowRight size={16} />
                    </button>
                </motion.div>
            </div>
        </motion.div>
    );
};

// --- Step 2: Neural Scan ---
const StepNeural = ({ onNext }) => {
    useEffect(() => {
        const timer = setTimeout(onNext, 4000); // Auto advance after 4s
        return () => clearTimeout(timer);
    }, [onNext]);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            className="w-full max-w-2xl text-center px-4"
        >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 text-primary mb-6">
                <Brain size={32} />
            </div>
            <h2 className="text-3xl md:text-4xl font-black mb-2">Neural Market Scan</h2>
            <p className="text-primary font-mono mb-8 animate-pulse text-sm md:text-base">Analyzing 450M+ Data Points...</p>

            <div className="grid grid-cols-8 md:grid-cols-12 gap-1 md:gap-2 h-48 md:h-64 p-2 md:p-4 border border-primary/30 rounded-2xl bg-black/50 overflow-hidden relative">
                {Array.from({ length: 96 }).map((_, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0.1, backgroundColor: "#333" }}
                        animate={{
                            opacity: [0.1, 1, 0.1],
                            backgroundColor: ["#333", "#ff4d4d", "#333"]
                        }}
                        transition={{
                            duration: Math.random() * 1 + 0.5,
                            repeat: Infinity,
                            delay: Math.random() * 2
                        }}
                        className="rounded-sm"
                    />
                ))}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black/80 px-4 py-3 md:px-6 md:py-4 rounded-xl border border-primary text-primary font-mono text-lg md:text-xl font-bold shadow-[0_0_50px_rgba(255,77,77,0.4)]">
                        PATTERN: SOLANA_FLIP
                        <div className="text-xs text-white mt-1">Confidence: 94.2%</div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// --- Step 3: Lobster Consensus ---
const StepLobster = ({ onNext }) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full max-w-2xl text-center"
        >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary/20 text-secondary mb-6">
                <Cpu size={32} />
            </div>
            <h2 className="text-4xl font-black mb-2">Lobster Consensus</h2>
            <p className="text-gray-400 mb-12">6 Autonomous Nodes voting on strategy.</p>

            <div className="relative h-[400px] w-full flex items-center justify-center">
                {/* Central Node */}
                <motion.div
                    animate={{ boxShadow: ["0 0 20px #00d4aa", "0 0 60px #00d4aa", "0 0 20px #00d4aa"] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-24 h-24 rounded-full bg-black border-4 border-secondary flex items-center justify-center z-20 relative cursor-pointer group"
                    onClick={onNext}
                >
                    <div className="text-secondary font-bold text-xl group-hover:scale-110 transition-transform">GO</div>
                    <div className="absolute -bottom-8 text-xs text-secondary font-mono opacity-0 group-hover:opacity-100 transition-opacity">Click to Execute</div>
                </motion.div>

                {/* Orbiting Agents */}
                {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1, rotate: 360 }}
                        transition={{
                            rotate: { duration: 10, repeat: Infinity, ease: "linear" },
                            opacity: { duration: 0.5, delay: i * 0.1 }
                        }}
                        className="absolute w-full h-full"
                    >
                        <div
                            className="absolute top-10 left-1/2 w-12 h-12 -ml-6 bg-black border border-secondary/50 rounded-xl flex items-center justify-center text-secondary shadow-[0_0_15px_rgba(0,212,170,0.3)]"
                        >
                            <Bot size={20} />
                        </div>
                    </motion.div>
                ))}
            </div>
        </motion.div>
    );
};

// --- Step 4: Liquidity ---
const StepLiquidity = ({ onNext }) => {
    useEffect(() => {
        const timer = setTimeout(onNext, 3500);
        return () => clearTimeout(timer);
    }, [onNext]);

    return (
        <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="w-full max-w-2xl text-center"
        >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 text-primary mb-6">
                <Share2 size={32} />
            </div>
            <h2 className="text-4xl font-black mb-8">Cross-Chain Execution</h2>

            <div className="flex items-center justify-center gap-8 md:gap-16">
                {/* Source */}
                <div className="text-center">
                    <div className="w-20 h-20 rounded-full border-2 border-white/20 flex items-center justify-center mb-4 text-xl font-bold">ETH</div>
                    <div className="text-sm text-gray-500">Liquidity Source</div>
                </div>

                {/* Flow Animation */}
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden relative max-w-xs">
                    <motion.div
                        className="absolute inset-0 bg-primary"
                        initial={{ x: '-100%' }}
                        animate={{ x: '100%' }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                    />
                </div>

                {/* Dest */}
                <div className="text-center">
                    <div className="w-20 h-20 rounded-full border-2 border-primary flex items-center justify-center mb-4 text-xl font-bold text-primary shadow-[0_0_30px_rgba(255,77,77,0.4)]">SOL</div>
                    <div className="text-sm text-primary">Target Executed</div>
                </div>
            </div>
        </motion.div>
    );
};

// --- Step 5: Result ---
const StepResult = ({ onRestart }) => {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg text-center bg-[#1A1A1A] border border-secondary/30 p-8 rounded-3xl shadow-[0_0_100px_rgba(0,212,170,0.1)]"
        >
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="w-24 h-24 mx-auto bg-secondary rounded-full flex items-center justify-center mb-6 shadow-2xl"
            >
                <Check size={48} className="text-black" />
            </motion.div>

            <h2 className="text-3xl font-black text-white mb-2">Trade Successful</h2>
            <p className="text-gray-400 mb-8">Protocol successfully identified and executed arbitrage.</p>

            <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="p-4 bg-black/50 rounded-xl">
                    <div className="text-xs text-muted-foreground uppercase">Net Profit</div>
                    <div className="text-2xl font-mono font-bold text-secondary">+$12,450</div>
                </div>
                <div className="p-4 bg-black/50 rounded-xl">
                    <div className="text-xs text-muted-foreground uppercase">Gas Fees</div>
                    <div className="text-xl font-mono font-bold text-white">$0.45</div>
                </div>
            </div>

            <button onClick={onRestart} className="w-full py-4 bg-secondary hover:bg-secondary/80 text-black font-bold rounded-xl transition-colors">
                Run New Simulation
            </button>
        </motion.div>
    );
};
